import { ScenariosCondition, ScenariosRequest } from '@api';
import { Alert, AlertType } from '@design/components/alert/Alert';
import { deepEqual,RangeHorizon  } from '@helpers';
import { useFilterActions } from '@terminal/hooks/use-filter-actions/useFilterActions';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useScenarios } from '@terminal/react-query-hooks/scenarios/useScenarios';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { formatScenarioConditionValue } from '@terminal/utils/condition-utils/condition-utils';
import { ScenarioDrawer } from '@terminal/widgets/scenario/components/scenario-drawer/ScenarioDrawer';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useScenarioState } from '../../hooks/use-scenario-state/useScenarioState';
import { useScenarioEntityFilters } from '../../hooks/use-scenario-store/filter-utils';
import { useScenarioStore } from '../../hooks/use-scenario-store/useScenarioStore';
import { getColumnFilters } from '../../utils/scenario-column-filters/scenario-column-filters';
import {
  checkIfFirstOptionIsNotChild,
  mapAssistantConditionsToConditions,
  mapFiltersToConditions,
} from '../../utils/scenario-conditions/scenario-conditions';
import { getConditionFilters } from '../../utils/scenario-conditions-filters/scenario-conditions-filters';
import { ScenarioTableData } from '../../utils/scenario-table-utils/scenario-table-utils';
import { ColumnFilters } from '../column-filters/ColumnFilters';
import { useScenarioAssistant } from '../scenario-assistant-modal/use-scenario-assistant/useScenarioAssistant';
import { ScenarioFilters } from '../scenario-filters/ScenarioFilters';
import { ScenarioResults } from '../scenario-results/ScenarioResults';
import { ScenarioToolbar } from '../scenario-toolbar/ScenarioToolbar';
import styles from './ScenarioConditions.module.scss';

export interface ScenarioConditionsProps {
  entitiesTags?: string[];
  onEditAssets: () => void;
  noAssetsButtonLabel?: string;
  onAddConditions?: () => void;
  onEditAssistantCondition?: (filterKey: string) => void;
  filtersChildren?: ReactNode;
}

const HORIZONS: string[] = [
  RangeHorizon.OneWeek,
  RangeHorizon.TwoWeeks,
  RangeHorizon.OneMonth,
  RangeHorizon.ThreeMonths,
  RangeHorizon.SixMonths,
  RangeHorizon.OneYear,
];

const SUMMARY_KEY = 'summary';

// eslint-disable-next-line max-lines-per-function
export const ScenarioConditions = ({
  entitiesTags = [],
  noAssetsButtonLabel,
  onEditAssets,
  onAddConditions,
  onEditAssistantCondition,
  filtersChildren,
}: ScenarioConditionsProps) => {
  const { t } = useTranslation(['scenario', 'analyze', 'widget', 'screener']);
  const navigate = useNavigate();

  const { deleteQueryParam, params, setQueryParams } = useQueryParams();
  const [activeHorizon, setActiveHorizon] = useState<string>(SUMMARY_KEY);
  const {
    conditions: assistantConditions,
    runConfirmedConditions,
    resetAskedQuestions,
  } = useScenarioAssistant(s => ({
    conditions: s.conditions.filter(c => c.active),
    runConfirmedConditions: s.runConfirmedConditions,
    resetConditions: s.resetConditions,
    resetAskedQuestions: s.resetAskedQuestions,
  }));

  const horizonKeys = useMemo(() => [SUMMARY_KEY, ...HORIZONS], []);

  const horizonKeyToDisplayNameMap = useMemo(() => {
    const map = new Map<string, string>();
    map.set(SUMMARY_KEY, t('scenario:summary'));

    HORIZONS.forEach(horizon => {
      map.set(horizon, t(`scenario:filters.horizons.${horizon}`));
    });

    return map;
  }, [t]);

  const horizonDisplayNames = useMemo(
    () =>
      [SUMMARY_KEY, ...HORIZONS].map(
        key => horizonKeyToDisplayNameMap.get(key) ?? key
      ),
    [horizonKeyToDisplayNameMap]
  );

  const isSummary = activeHorizon === SUMMARY_KEY;

  const {
    filterGroups,
    initStore,
    lastEntitiesTags,
    setLastEntitiesTags,
    lastConditions,
    setLastConditions,
    lastScenariosData,
    lastActiveFilterOptions,
    setLastActiveFilterOptions,
    hasExceededUsageLimit,
  } = useScenarioStore(state => ({
    filterGroups: state.filterGroups,
    initStore: state.initStore,
    lastEntitiesTags: state.lastEntitiesTags,
    setLastEntitiesTags: state.setLastEntitiesTags,
    lastConditions: state.lastConditions,
    setLastConditions: state.setLastConditions,
    lastScenariosData: state.lastScenariosData,
    lastActiveFilterOptions: state.lastActiveFilterOptions,
    setLastActiveFilterOptions: state.setLastActiveFilterOptions,
    hasExceededUsageLimit: state.hasExceededUsageLimit,
  }));

  const {
    activeFilterOptions: activeScenarioConditionsFilterOptions,
    activeFilters: activeScenarioConditionsFilters,
  } = filterGroups.SCENARIO_CONDITIONS;

  const {
    activeFilters: activeScenarioAssetFilters,
    activeFilterOptions: activeScenarioAssetFilterOptions,
  } = filterGroups.ASSET_FILTERS;

  const { resetActiveFilterOptions: resetActiveConditionsFilterOptions } =
    useFilterActions({
      store: useScenarioStore,
      group: 'SCENARIO_CONDITIONS',
    });

  useEffect(() => {
    const hasActiveFilterOptionsChanged = !deepEqual(
      activeScenarioAssetFilterOptions,
      lastActiveFilterOptions
    );

    if (hasActiveFilterOptionsChanged) {
      resetActiveConditionsFilterOptions();
    }
  }, []);

  useEffect(() => {
    return () => {
      setLastActiveFilterOptions(activeScenarioAssetFilterOptions);
    };
  }, [activeScenarioAssetFilterOptions]);

  const { isLoading, error, data, abort, sendMessage } = useScenarios();
  const scenarioEntityFilters = useScenarioEntityFilters();

  useEffect(() => {
    if (
      !activeScenarioConditionsFilters.length &&
      !activeScenarioAssetFilters.length
    ) {
      initStore([
        {
          group: 'SCENARIO_CONDITIONS',
          schemas: getConditionFilters(t),
        },
        {
          group: 'COLUMN_FILTERS',
          schemas: getColumnFilters(t),
        },
        {
          group: 'ASSET_FILTERS',
          schemas: scenarioEntityFilters,
        },
      ]);
    }
  }, []);

  useEffect(() => {
    setActiveHorizon(SUMMARY_KEY);
  }, [data]);

  const onAbort = () => {
    abort();
    setLastConditions(conditions.slice(0, -1));
  };

  const activeFilterOptionsFiltered =
    activeScenarioConditionsFilterOptions.filter(filterOption =>
      checkIfFirstOptionIsNotChild(filterOption.options)
    );

  const conditions: ScenariosCondition[] = useMemo(
    () => [
      ...mapFiltersToConditions(activeFilterOptionsFiltered),
      ...mapAssistantConditionsToConditions(assistantConditions),
    ],
    [activeFilterOptionsFiltered, assistantConditions]
  );

  const { entitiesOrConditionsChanged } = useScenarioState({
    conditions,
    lastConditions,
    entitiesTags,
    lastEntitiesTags,
  });

  const scenarioResultsData = entitiesOrConditionsChanged
    ? data
    : lastScenariosData;

  const onRunConditions = () => {
    setLastConditions(conditions);
    setLastEntitiesTags(entitiesTags);

    const formattedConditions = conditions.map(formatScenarioConditionValue);
    const requestPayload: ScenariosRequest = {
      entities: entitiesTags.map(tag => ({
        tag,
      })),
      conditions: formattedConditions,
      horizons: HORIZONS,
      start_horizon: RangeHorizon.ZeroDay,
      end_horizon: RangeHorizon.OneYear,
      skip_report: false,
      non_overlapping: true,
    };

    sendMessage(requestPayload);
    runConfirmedConditions();
  };

  const onRowClick = (item: ScenarioTableData) => {
    setQueryParams({
      [queryKeys.entityTag]: item.entity.tag,
      [queryKeys.activeDrawer]: 'scenario',
    });
  };

  const closeDrawer = () => {
    deleteQueryParam([queryKeys.entityTag, queryKeys.activeDrawer], {
      replace: true,
    });
  };

  const hasData = scenarioResultsData.some(item => item.predictions.length > 0);

  const selectedData = useMemo(() => {
    return scenarioResultsData.find(
      item => item.entity.tag === params.entityTag
    );
  }, [params.entityTag, scenarioResultsData]);

  return (
    <>
      {hasExceededUsageLimit && (
        <Alert
          className={styles.alert}
          variant="inline"
          type={AlertType.Warning}
          title={t('scenario:warning.dailyLimitReached')}
          message={t('scenario:warning.yourPlanHasALimit')}
          primaryBtn={{
            variant: 'primary',
            onClick: () => {
              navigate(appPaths.settings.subscription);
            },
            label: t('scenario:buttonLabels.upgrade'),
            size: 'large',
          }}
        />
      )}
      <ScenarioFilters
        filtersChildren={filtersChildren}
        onEditAssistantCondition={onEditAssistantCondition}
        resetAskedQuestions={resetAskedQuestions}
      />
      <ColumnFilters />
      <ScenarioToolbar
        activeHorizon={activeHorizon}
        entitiesTags={entitiesTags}
        horizonLabels={horizonDisplayNames}
        horizonKeys={horizonKeys}
        setActiveHorizon={setActiveHorizon}
        hasData={hasData}
      />

      <ScenarioResults
        activeHorizon={activeHorizon}
        disabled={entitiesOrConditionsChanged}
        data={scenarioResultsData}
        entitiesTags={entitiesTags}
        noAssetsButtonLabel={
          noAssetsButtonLabel ?? t('scenario:buttonLabels.goBackButtonLabel')
        }
        hasError={error}
        horizons={HORIZONS}
        isLoading={isLoading}
        isSummary={isSummary}
        conditionsCount={conditions.length}
        lastConditions={lastConditions}
        onAbort={onAbort}
        onEditAssets={onEditAssets}
        onRowClick={onRowClick}
        onRunConditions={onRunConditions}
        onAddConditions={onAddConditions}
      />

      {!!selectedData && !!params.entityTag && (
        <ScenarioDrawer
          activeHorizon={activeHorizon}
          closeDrawer={closeDrawer}
          horizons={HORIZONS}
          isSummary={isSummary}
          lastConditions={lastConditions}
          scenario={selectedData}
          entityTag={params.entityTag}
        />
      )}
    </>
  );
};
